import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useDeliveryZone } from '../../contexts/DeliveryZoneContext';
import { useToast } from '../../contexts/ToastContext';
import { Zone } from '../../types';
import { isRestaurantOpen } from '../../utils/restaurant';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const {
    items,
    total,
    itemCount,
    deliveryFee,
    actions,
    deliveryFeeCalculationStatus,
    deliveryFeeError,
    restaurant,
  } = useCart();
  const { currentZone, availableZones, setCurrentZone } = useDeliveryZone();
  const { success } = useToast();
  const [showZoneSelector, setShowZoneSelector] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isOnCheckout = pathname === '/checkout';
  const restaurantClosed = restaurant !== null && !isRestaurantOpen(restaurant);

  const handleRemoveItem = (itemId: string, itemName: string) => {
    actions.removeItem(itemId);
    success(`${itemName} removed from cart`);
  };

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  // Shared cart content component
  const CartContent = () => (
    <div className="flex flex-col h-full mt-4">
      {/* Balance Card - Sleek, modern design with MINA theme and glass effect */}
      <div className="bg-mina-sand/80 backdrop-blur-md rounded-mina p-5 mb-4 border border-mina-border shadow-mina-sm">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-mina-muted text-xs uppercase tracking-wide mb-1.5">Your Cart</p>
            <p className="text-3xl font-bold text-mina-espresso mb-1">
              ${(total + (deliveryFee ?? currentZone?.delivery_cost ?? 0)).toFixed(2)}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-mina-ink">
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </span>
              {items.length > 0 && (
                <button
                  onClick={actions.clearCart}
                  className="text-mina-muted hover:text-mina-mustard text-xs underline transition-colors"
                >
                  Clear cart
                </button>
              )}
            </div>
          </div>
          <div className="w-16 h-16 bg-mina-orange/20 rounded-2xl flex items-center justify-center ml-4">
            <svg
              className="w-8 h-8 text-mina-mustard"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5 6m0 0h9"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Delivery Zone Card */}
      <div className="mina-card p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-mina-mustard"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="text-xs font-medium text-mina-muted uppercase">Delivering To</span>
          </div>
          <button
            onClick={() => setShowZoneSelector(!showZoneSelector)}
            className="text-mina-mustard text-xs font-medium hover:text-mina-espresso transition-colors"
          >
            {showZoneSelector ? 'Cancel' : 'Change'}
          </button>
        </div>

        {showZoneSelector ? (
          <div className="space-y-2">
            <select
              value={currentZone?.id || ''}
              onChange={(e) => {
                const selectedZone = availableZones.find(
                  (zone: Zone) => zone.id === e.target.value
                );
                if (selectedZone) {
                  setCurrentZone(selectedZone);
                  setShowZoneSelector(false);
                }
              }}
              className="mina-input text-sm"
            >
              <option value="" disabled>
                Select your delivery zone
              </option>
              {availableZones.map((zone: Zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <p className="font-semibold text-mina-espresso text-sm">
              {currentZone?.name || 'No zone selected'}
            </p>
          </div>
        )}
      </div>

      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="w-20 h-20 bg-mina-offwhite rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-10 h-10 text-mina-muted"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5 6m0 0h9"
                />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-mina-espresso mb-2">Your cart is empty</h3>
            <p className="text-sm text-mina-muted">Add items to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.id} className="mina-card p-4 overflow-hidden">
                <div className="flex gap-3">
                  {/* Item image placeholder */}
                  <div className="w-16 h-16 bg-gradient-to-br from-mina-orange/10 to-mina-sand rounded-mina flex-shrink-0 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-mina-mustard/40"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <h4 className="font-semibold text-mina-espresso text-sm">
                          {item.name}
                        </h4>
                      </div>
                      <p className="text-sm font-semibold text-mina-espresso whitespace-nowrap flex-shrink-0 ml-2">
                        ${item.totalPrice.toFixed(2)}
                      </p>
                    </div>

                    {/* Customizations */}
                    {item.customizations && item.customizations.length > 0 && (
                      <div className="mb-2">
                        {item.customizations.map((customization, idx) => (
                          <div key={idx} className="text-xs text-mina-muted">
                            {customization.selectedOptions.map((option, optIdx) => (
                              <span key={optIdx}>
                                {option.name}
                                {option.price > 0 && ` (+$${option.price.toFixed(2)})`}
                                {optIdx < customization.selectedOptions.length - 1 && ', '}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Special instructions */}
                    {item.specialInstructions && (
                      <p className="text-xs text-mina-muted italic mb-2">
                        Note: {item.specialInstructions}
                      </p>
                    )}

                    {/* Quantity controls */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center bg-mina-offwhite rounded-mina border border-mina-border">
                        <button
                          onClick={() => {
                            if (item.quantity > 1) {
                              actions.updateQuantity(item.id, item.quantity - 1);
                            } else {
                              handleRemoveItem(item.id, item.name);
                            }
                          }}
                          className="p-1.5 hover:bg-mina-sand transition-colors rounded-l-mina"
                          aria-label={item.quantity > 1 ? 'Decrease quantity' : 'Remove item'}
                        >
                          <svg
                            className="w-3 h-3 text-mina-ink"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M20 12H4"
                            />
                          </svg>
                        </button>
                        <span className="px-3 text-sm font-medium text-mina-espresso min-w-[2rem] text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => actions.updateQuantity(item.id, item.quantity + 1)}
                          className="p-1.5 hover:bg-mina-sand transition-colors rounded-r-mina"
                          aria-label="Increase quantity"
                        >
                          <svg
                            className="w-3 h-3 text-mina-ink"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                        </button>
                      </div>
                      <button
                        onClick={() => handleRemoveItem(item.id, item.name)}
                        className="p-1.5 text-mina-muted hover:text-mina-chili transition-colors rounded"
                        aria-label={`Remove ${item.name} from cart`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delivery Fee - always visible */}
      <div className="mt-4 pt-4 border-t border-mina-border">
        <div className="flex justify-between text-sm text-mina-muted mb-1">
          <span>Delivery Fee</span>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-mina-ink">
              ${(deliveryFee ?? currentZone?.delivery_cost ?? 0).toFixed(2)}
            </span>
            {deliveryFeeCalculationStatus === 'calculating' && (
              <svg className="animate-spin w-3 h-3 text-mina-muted" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
          </div>
        </div>
        {deliveryFeeError && <p className="text-xs text-mina-chili mb-2">{deliveryFeeError}</p>}
      </div>

      {/* Order Summary + Checkout - only when items exist */}
      {items.length > 0 && (
        <div className="pt-3 border-t border-mina-border">
          <div className="flex justify-between text-sm text-mina-muted mb-4">
            <span>Subtotal</span>
            <span className="font-medium text-mina-ink">${total.toFixed(2)}</span>
          </div>

          {restaurantClosed && (
            <p className="text-xs text-red-600 font-medium mb-3">
              {restaurant?.name} is currently closed. Orders cannot be placed right now.
            </p>
          )}

          {/* Checkout Button */}
          {!isOnCheckout && (
            <button
              onClick={handleCheckout}
              disabled={restaurantClosed}
              className={`mina-btn-primary w-full text-base py-3.5 ${restaurantClosed ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              Proceed to Checkout • ${(total + (deliveryFee ?? currentZone?.delivery_cost ?? 0)).toFixed(2)}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile Drawer (slides in from right) */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop - Mobile only */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
              aria-hidden="true"
            />

            {/* Mobile Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-mina-cream shadow-xl z-50 flex flex-col lg:hidden"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cart-drawer-title"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 bg-mina-surface border-b border-mina-border">
                <h2 id="cart-drawer-title" className="text-lg font-bold text-mina-espresso">
                  Cart ({itemCount})
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 text-mina-muted hover:text-mina-ink transition-colors rounded-mina hover:bg-mina-offwhite"
                  aria-label="Close cart"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-hidden p-4 bg-mina-cream">
                <CartContent />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Fixed Sidebar - Always visible on large screens */}
      <div className="hidden lg:flex fixed right-0 top-16 h-[calc(100vh-4rem)] w-96 bg-mina-cream border-l border-mina-border z-30 flex-col overflow-hidden">
        <div className="p-4 h-full overflow-y-auto">
          <CartContent />
        </div>
      </div>
    </>
  );
};

export default CartDrawer;
